/**
 * Favicon Badge Manager
 *
 * Adds a Google-Chat style red dot overlay to the favicon when there are unread messages.
 * The badge blinks until the user refocuses the tab.
 *
 * Features:
 * - Red dot badge overlay on favicon when unread count > 0
 * - Blinking animation (1 second interval) when tab is not focused
 * - Automatic stop on tab focus/visibility
 * - Updates page title with unread count (e.g., "(3) Zarity")
 * - Syncs with ping store for patient and teams unread counts
 *
 * Usage:
 * The favicon badge is automatically managed by the ping store.
 * When unread counts change, the badge is updated automatically.
 *
 * Manual usage (if needed):
 * ```ts
 * import { faviconBadge } from '@/lib/favicon-badge';
 *
 * // Set badge count
 * faviconBadge.setBadgeCount(5);
 *
 * // Remove badge
 * faviconBadge.removeBadge();
 *
 * // Reset to original
 * faviconBadge.reset();
 * ```
 */

// Resolve a CSS custom property to a full color string. Falls back to the
// provided value when the variable can't be read (SSR / missing token).
function resolveToken(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v ? `oklch(${v})` : fallback;
}

class FaviconBadgeManager {
  private originalFavicon: string = "";
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private blinkInterval: number | null = null;
  private isBlinking: boolean = false;
  private showBadge: boolean = false;
  private badgeCount: number = 0;
  private originalTitle: string = "";
  private hasFocus: boolean = true;

  constructor() {
    this.initialize();
  }

  private initialize() {
    // Store the original favicon and title
    const faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
    if (faviconLink) {
      this.originalFavicon = faviconLink.href;
    }
    this.originalTitle = document.title;

    // Create canvas for drawing the favicon with badge
    this.canvas = document.createElement("canvas");
    this.canvas.width = 32;
    this.canvas.height = 32;
    this.ctx = this.canvas.getContext("2d");

    // Listen for focus/blur events
    this.setupFocusListeners();
  }

  private setupFocusListeners() {
    // Handle tab focus/blur
    window.addEventListener("focus", () => {
      this.hasFocus = true;
      this.stopBlinking();
      if (this.badgeCount === 0) {
        this.removeBadge();
      }
    });

    window.addEventListener("blur", () => {
      this.hasFocus = false;
    });

    // Handle visibility change (tab switching)
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.hasFocus = true;
        this.stopBlinking();
        if (this.badgeCount === 0) {
          this.removeBadge();
        }
      } else {
        this.hasFocus = false;
      }
    });
  }

  private drawFavicon(withBadge: boolean) {
    if (!this.ctx || !this.canvas) return;

    // Clear the canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Load and draw the original favicon
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      if (!this.ctx || !this.canvas) return;

      // Draw the original favicon
      this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);

      // Draw the red badge dot if needed
      if (withBadge) {
        const badgeSize = 10;
        const badgeX = this.canvas.width - badgeSize;
        const badgeY = 0;

        // Draw red circle
        this.ctx.beginPath();
        this.ctx.arc(
          badgeX + badgeSize / 2,
          badgeY + badgeSize / 2,
          badgeSize / 2,
          0,
          2 * Math.PI
        );
        this.ctx.fillStyle = resolveToken("--destructive", "#ef4444");
        this.ctx.fill();

        // Outline contrasts against any underlying favicon — uses --foreground
        // (cream in dark, deep sepia in light) so the dot pops in both themes.
        this.ctx.strokeStyle = resolveToken("--foreground", "#ffffff");
        this.ctx.lineWidth = 1.5;
        this.ctx.stroke();
      }

      // Update the favicon
      this.updateFaviconLink();
    };

    img.onerror = () => {
      // If image fails to load, just draw the badge on a blank canvas
      if (withBadge && this.ctx) {
        const badgeSize = 10;
        const badgeX = this.canvas!.width - badgeSize;
        const badgeY = 0;

        this.ctx.beginPath();
        this.ctx.arc(
          badgeX + badgeSize / 2,
          badgeY + badgeSize / 2,
          badgeSize / 2,
          0,
          2 * Math.PI
        );
        this.ctx.fillStyle = resolveToken("--destructive", "#ef4444");
        this.ctx.fill();
        this.ctx.strokeStyle = resolveToken("--foreground", "#ffffff");
        this.ctx.lineWidth = 1.5;
        this.ctx.stroke();
      }
      this.updateFaviconLink();
    };

    img.src = this.originalFavicon || "/favicon-32.png";
  }

  private updateFaviconLink() {
    if (!this.canvas) return;

    const faviconUrl = this.canvas.toDataURL("image/png");
    let faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel*="icon"]');

    if (!faviconLink) {
      faviconLink = document.createElement("link");
      faviconLink.rel = "icon";
      document.head.appendChild(faviconLink);
    }

    faviconLink.href = faviconUrl;
  }

  private updateTitle() {
    if (this.badgeCount > 0 && !this.hasFocus) {
      document.title = `(${this.badgeCount}) ${this.originalTitle}`;
    } else {
      document.title = this.originalTitle;
    }
  }

  private startBlinking() {
    if (this.isBlinking || this.hasFocus) return;

    this.isBlinking = true;
    let visible = true;

    this.blinkInterval = window.setInterval(() => {
      // Stop blinking if user focuses the tab
      if (this.hasFocus) {
        this.stopBlinking();
        return;
      }

      visible = !visible;
      this.drawFavicon(visible);

      // Also blink the title
      if (visible) {
        this.updateTitle();
      } else {
        document.title = this.originalTitle;
      }
    }, 1000); // Blink every second
  }

  private stopBlinking() {
    if (!this.isBlinking) return;

    this.isBlinking = false;
    if (this.blinkInterval !== null) {
      clearInterval(this.blinkInterval);
      this.blinkInterval = null;
    }

    // Show the badge in its final state if count > 0
    if (this.badgeCount > 0) {
      this.drawFavicon(true);
    } else {
      this.drawFavicon(false);
    }

    this.updateTitle();
  }

  /**
   * Set the badge count and update the favicon
   * @param count - Number of unread messages
   */
  public setBadgeCount(count: number) {
    const previousCount = this.badgeCount;
    this.badgeCount = Math.max(0, count);

    // If count changed from 0 to positive, start blinking
    if (previousCount === 0 && this.badgeCount > 0) {
      this.showBadge = true;
      this.drawFavicon(true);
      this.updateTitle();

      // Only blink if tab is not focused
      if (!this.hasFocus) {
        this.startBlinking();
      }
    }
    // If count changed from positive to 0, remove badge
    else if (previousCount > 0 && this.badgeCount === 0) {
      this.removeBadge();
    }
    // If count is still positive but changed, update title
    else if (this.badgeCount > 0) {
      this.updateTitle();
      // Ensure badge is visible
      this.drawFavicon(true);
    }
  }

  /**
   * Add a badge dot to the favicon
   */
  public addBadge() {
    this.setBadgeCount(1);
  }

  /**
   * Remove the badge dot from the favicon
   */
  public removeBadge() {
    this.showBadge = false;
    this.badgeCount = 0;
    this.stopBlinking();
    this.drawFavicon(false);
    this.updateTitle();
  }

  /**
   * Reset to the original favicon (without badge)
   */
  public reset() {
    this.removeBadge();

    const faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
    if (faviconLink && this.originalFavicon) {
      faviconLink.href = this.originalFavicon;
    }
  }
}

// Export a singleton instance
export const faviconBadge = new FaviconBadgeManager();
